import { StepName } from "components/Signup/steps/types";
import { State } from "components/Signup/store/state/types";
import { getDefaultHealthInsurance } from "components/Signup/store/state/utils";
import { isFuture } from "date-fns";
import { getEarliestAllowedStartDate, validStartDate } from "utils/insurance";
import { parseRouterQueryToObject } from "utils/objectToQueryString";

export function applyStateFromQuery(
  props: Record<string, any>,
  initialState: State,
  breeds: {
    id: string;
    name: string;
    lifeAgeGroup: number;
  }[]
): State {
  const parsedRouterQuery = parseRouterQueryToObject(props, [
    "address.zip",
    "userMunicipality",
    "phone",
    "region",
    "name",
    "healthHistory",
  ]);

  const animalKeys = Object.keys(initialState.animals[0]);
  const insuranceKeys = Object.keys(getDefaultHealthInsurance());
  const metadataKeys = Object.keys(initialState.metadata);
  const customerKeys = Object.keys(initialState.customer);
  const addressKeys = Object.keys(initialState.customer.address || {});
  const navigationKeys = Object.keys(initialState.navigation);

  return Object.entries(parsedRouterQuery).reduce<State>((s, [key, value]) => {
    if (typeof value === "undefined") {
      return s;
    }

    // Backwards compatible
    if (key === "nextStep") {
      return {
        ...s,
        navigation: { ...s.navigation, activeStep: value as StepName },
      };
    }

    if (navigationKeys.includes(key)) {
      return { ...s, navigation: { ...s.navigation, [key]: value } };
    }

    if (addressKeys.includes(key)) {
      return {
        ...s,
        customer: {
          ...s.customer,
          address: { ...s.customer.address!, [key]: value },
        },
      };
    }

    if (metadataKeys.includes(key)) {
      return { ...s, metadata: { ...s.metadata, [key]: value } };
    }

    if (customerKeys.includes(key)) {
      return { ...s, customer: { ...s.customer, [key]: value } };
    }

    if (insuranceKeys.includes(key)) {
      // Some browsers lowercase the query parameter when it's used directly on
      // the path segment, i.e /signup?discountCode.
      // https://lassieco.slack.com/archives/C019CN57ZGW/p1751644805248869
      if (key === "discountCode" && typeof value === "string") {
        value = value.toUpperCase();
      }

      if (key === "startDate") {
        const newStartDate = parsedRouterQuery.birthdate
          ? validStartDate({
              birthday: props.birthdate,
              startDate: props.startDate,
            })
          : !isFuture(new Date(props.startDate))
          ? getEarliestAllowedStartDate().toISOString().split("T")[0]
          : (value as string);

        return {
          ...s,
          animals: [
            {
              ...s.animals[0],
              insurances: [
                {
                  ...s.animals[0].insurances[0],
                  startDate: newStartDate,
                },
              ],
            },
          ],
        };
      }

      return {
        ...s,
        animals: [
          {
            ...s.animals[0],
            insurances: [{ ...s.animals[0].insurances[0], [key]: value }],
          },
        ],
      };
    }

    if (animalKeys.includes(key)) {
      if (key === "breed") {
        // Transform breed id to breed object
        if (typeof value === "string") {
          if (value.includes(",")) {
            const [firstBreed, secondBreed] = value.split(",");

            return {
              ...s,
              animals: [
                {
                  ...s.animals[0],
                  breed:
                    breeds?.find((b) => b.id === "DogBreed-blandras") ?? null,
                  mixedBreedFHalf:
                    breeds?.find((b) => b.id === firstBreed) ?? null,
                  mixedBreedSHalf:
                    breeds?.find((b) => b.id === secondBreed) ?? null,
                },
              ],
            };
          } else {
            return {
              ...s,
              animals: [
                {
                  ...s.animals[0],
                  breed: breeds?.find((b) => b.id === value) ?? null,
                },
              ],
            };
          }
        }
      }
      return {
        ...s,
        animals: [{ ...s.animals[0], [key]: value }],
      };
    }

    return s;
  }, initialState);
}
